import React from 'react';
import { Box, Typography, Grid, InputAdornment } from '@mui/material';
import { GridContainer, GridItem, TextField } from '../../../components/shared';
import { getCurrency, getDateFormat } from '../../../utils/common-methods';
import { CURRENCY_CONSTANT } from '../../../constants/CurrencyConstants';
import { LEASE_COIN_COST_UNIT_OF_MEASURES, RENT_TYPE_CONFIG, RENT_TYPES } from '../../../constants/rentDetailsConfig';
import { useGetLeaseByIdWithVersionQuery } from '../../../services/Lease/LeaseService';
import { CircularProgress } from '@mui/material';
import useStyles from '../../../components/RentDetailsForm/style';

export const LeaseDetailsVersionView = ({ leaseId, version }) => {
  const classes = useStyles();
  const { data: leaseData, isLoading, error } = useGetLeaseByIdWithVersionQuery({ leaseId, version });

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box px={3}>
        <Typography color="error">Error loading lease details: {error.message}</Typography>
      </Box>
    );
  }

  if (!leaseData) {
    return (
      <Box px={3}>
        <Typography>No lease data found</Typography>
      </Box>
    );
  }

  const getSelectDisplayValue = (value, options) => {
    const option = options.find((opt) => opt.value === value);
    return option ? option.text : value || 'N/A';
  };
  const formatCurrency = (value) => {
    if (value === null || value === undefined || value === '') return '';
    return `${Number(value).toFixed(2)}`;
  };
  const formatPercentage = (value) => {
    if (value === null || value === undefined || value === '') return '';
    const percentageValue = Number(value) * 100;
    return parseFloat(percentageValue.toFixed(2)).toString();
  };

  const showTierSummary =
    leaseData.rentType === 'RATE_ONLY_TIERED' ||
    leaseData.rentType === 'RATE_ONLY_TIERED_TERM_COMBO' ||
    leaseData.rentType === 'FEE_PLUS_TIERED_RATE';

  const sublocationNames =
    leaseData.contractSublocations?.map((subloc) => subloc?.sublocation?.name).filter(Boolean) || [];

  const location = leaseData.location || {};

  const dynamicTextFields = leaseData.contractAssetGameCategoryRates || [];

  return (
    <Box
      className={classes.rentDetailsForm}
      data-testid="leaseDetailsVersionView"
      px={4}
      pb={4}
      sx={{
        '& .MuiInputBase-input.Mui-disabled': {
          WebkitTextFillColor: '#000000 !important',
          color: '#000000 !important',
        },
        '& .MuiInputLabel-root.Mui-disabled': {
          color: 'rgba(0, 0, 0, 0.87) !important',
        },
      }}
    >
      <GridContainer rowSpacing={3} columnSpacing={{ xs: 0, sm: 0, md: 6 }}>
        <GridItem xs={12} md={12} pl={0} my={2}>
          <Typography variant="h2">Lease Details</Typography>
        </GridItem>

        <GridItem xs={12} pl={0}>
          <TextField label="Sub Location" value={sublocationNames.join(', ') || 'N/A'} disabled multiline />
        </GridItem>

        <GridItem xs={12} md={4} pl={0}>
          <TextField label="Lease Begins" value={getDateFormat(leaseData.termStartDate) || 'N/A'} disabled />
        </GridItem>

        <GridItem xs={12} md={4} pl={0}>
          <TextField label="Lease Ends" value={getDateFormat(leaseData.termEndDate) || 'N/A'} disabled />
        </GridItem>

        <GridItem xs={12} md={4} pl={0}>
          <TextField label="Lease Name" value={leaseData.lease_name || 'N/A'} disabled />
        </GridItem>

        <GridItem xs={12} md={4} pl={0}>
          <TextField label="In Effect From" value={getDateFormat(leaseData.in_effect_from) || ''} disabled />
        </GridItem>

        <GridItem xs={12} md={4} pl={0}>
          <TextField label="In Effect To" value={getDateFormat(leaseData.in_effect_to) || ''} disabled />
        </GridItem>

        <GridItem xs={12} md={4} pl={0}>
          <TextField label="Contract Link" value={leaseData.contractLink || 'N/A'} disabled multiline maxRows={4} />
        </GridItem>

        <GridItem xs={12} md={4} pl={0}>
          <TextField label="Notes" value={leaseData.notes || 'N/A'} disabled multiline />
        </GridItem>

        <Grid pl={2} my={2} container spacing={2} justifyContent="flex-end">
          {[
            { label: 'Location :', value: location.locationName || 'N/A' },
            { label: 'DBA :', value: leaseData.dba || 'N/A' },
            {
              label: 'Address :',
              value: (() => {
                const addressParts = [
                  location.address1,
                  location.address2,
                  location.city?.name,
                  location.state?.name,
                  location.postcode,
                  location.country?.name,
                ].filter(Boolean);
                return addressParts.length > 0 ? addressParts.join(', ') : 'N/A';
              })(),
            },
            { label: 'Booth Info :', value: leaseData.boothInfo || 'N/A' },
            {
              label: 'Space Identifier :',
              value:
                leaseData.contractSublocations
                  ?.map((cs) => cs.sublocation?.spaceIdentifier)
                  .filter(Boolean)
                  .join(', ') || 'N/A',
            },
            { label: '', value: '' },
          ].map((item, index) => (
            <GridItem key={index} xs={12} md={6} pl={1} my={-1}>
              <Box display="flex">
                <Typography fontWeight="bold" sx={{ minWidth: '133px' }}>
                  {item.label}
                </Typography>
                <Typography>{item.value}</Typography>
              </Box>
            </GridItem>
          ))}
        </Grid>

        <GridItem xs={12} md={12} pl={0} my={2}>
          <Typography variant="h2">Payment Terms</Typography>
        </GridItem>

        <GridItem xs={12} md={4} pl={0} order={1}>
          <TextField label="Rent Type" value={getSelectDisplayValue(leaseData.rentType, RENT_TYPES)} disabled />
        </GridItem>

        <GridItem xs={12} md={4} pl={0} order={dynamicTextFields?.length > 0 ? 19 + dynamicTextFields?.length : 19}>
          <TextField
            label="Rent Options"
            value={
              leaseData.contractRentOptions
                ?.map((cro) => cro?.rentOption?.name)
                .filter(Boolean)
                .join(', ') || 'N/A'
            }
            disabled
          />
        </GridItem>

        {RENT_TYPE_CONFIG.rentFee.includes(leaseData.rentType) && (
          <GridItem xs={12} md={4} pl={0} order={2}>
            <TextField
              label="Rent Fee"
              value={formatCurrency(leaseData.fee)}
              disabled
              InputProps={{
                startAdornment: <InputAdornment position="start">{getCurrency(CURRENCY_CONSTANT.USD)}</InputAdornment>,
              }}
            />
          </GridItem>
        )}

        {RENT_TYPE_CONFIG.rentRate.includes(leaseData.rentType) && (
          <GridItem xs={12} md={4} pl={0} order={3}>
            <TextField
              label="Rent Rate"
              value={formatPercentage(leaseData.rate)}
              disabled
              InputProps={{
                endAdornment: <InputAdornment position="end">%</InputAdornment>,
              }}
            />
          </GridItem>
        )}

        {RENT_TYPE_CONFIG.CAMFee.includes(leaseData.rentType) && (
          <GridItem xs={12} md={4} pl={0} order={4}>
            <TextField
              label="CAM Fee"
              value={formatCurrency(leaseData.CAMFee)}
              disabled
              InputProps={{
                startAdornment: <InputAdornment position="start">{getCurrency(CURRENCY_CONSTANT.USD)}</InputAdornment>,
              }}
            />
          </GridItem>
        )}

        {RENT_TYPE_CONFIG.CAMRate.includes(leaseData.rentType) && (
          <GridItem xs={12} md={4} pl={0} order={5}>
            <TextField
              label="CAM Rate"
              value={formatPercentage(leaseData.CAMRate)}
              disabled
              InputProps={{
                endAdornment: <InputAdornment position="end">%</InputAdornment>,
              }}
            />
          </GridItem>
        )}

        {RENT_TYPE_CONFIG.dynamicTextFields.includes(leaseData.rentType) && (
          <>
            {dynamicTextFields.map((gameRate, index) => {
              const order = index + 1 + 11;
              return (
                <GridItem key={order} order={order} xs={12} md={4} pl={0}>
                  <TextField
                    label={gameRate.gameCategory?.name || `Game Category ${index + 1}`}
                    value={formatCurrency(gameRate.rentRate)}
                    disabled
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">{getCurrency(CURRENCY_CONSTANT.USD)}</InputAdornment>
                      ),
                    }}
                  />
                </GridItem>
              );
            })}
          </>
        )}

        {RENT_TYPE_CONFIG.breakpointRevenue.includes(leaseData.rentType) && (
          <GridItem xs={12} md={4} pl={0} order={dynamicTextFields?.length > 0 ? 12 + dynamicTextFields?.length : 12}>
            <TextField
              label="Breakpoint Revenue"
              value={formatCurrency(leaseData.breakpointRevenue)}
              disabled
              InputProps={{
                startAdornment: <InputAdornment position="start">{getCurrency(CURRENCY_CONSTANT.USD)}</InputAdornment>,
              }}
            />
          </GridItem>
        )}

        {RENT_TYPE_CONFIG.breakpointRate.includes(leaseData.rentType) && (
          <GridItem xs={12} md={4} pl={0} order={dynamicTextFields?.length > 0 ? 13 + dynamicTextFields?.length : 13}>
            <TextField
              label="Breakpoint Rate"
              value={formatPercentage(leaseData.breakpointRate)}
              disabled
              InputProps={{
                endAdornment: <InputAdornment position="end">%</InputAdornment>,
              }}
            />
          </GridItem>
        )}

        {RENT_TYPE_CONFIG.resetTerm.includes(leaseData.rentType) && (
          <GridItem xs={12} md={4} pl={0} order={dynamicTextFields?.length > 0 ? 14 + dynamicTextFields?.length : 14}>
            <TextField label="Reset Term" value={leaseData.resetTerm || 'N/A'} disabled />
          </GridItem>
        )}

        {RENT_TYPE_CONFIG.payFrequency.includes(leaseData.rentType) && (
          <GridItem xs={12} md={4} pl={0} order={dynamicTextFields?.length > 0 ? 15 + dynamicTextFields?.length : 15}>
            <TextField label="Pay Frequency" value={leaseData.payFrequency || 'N/A'} disabled />
          </GridItem>
        )}

        <GridItem xs={12} md={4} pl={0} order={dynamicTextFields?.length > 0 ? 16 + dynamicTextFields?.length : 16}>
          <TextField
            label="Coin Cost/Rate"
            value={formatCurrency(leaseData.coinCost)}
            disabled
            InputProps={
              leaseData.coinCostUom === LEASE_COIN_COST_UNIT_OF_MEASURES.PERCENTAGE
                ? { endAdornment: <InputAdornment position="end">%</InputAdornment> }
                : {
                    startAdornment: (
                      <InputAdornment position="start">{getCurrency(CURRENCY_CONSTANT.USD)}</InputAdornment>
                    ),
                  }
            }
          />
        </GridItem>

        <GridItem xs={12} md={4} pl={0} order={dynamicTextFields?.length > 0 ? 17 + dynamicTextFields?.length : 17}>
          <TextField
            label="CC Default Rate"
            value={formatPercentage(leaseData.creditCardFeeRate)}
            disabled
            InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
          />
        </GridItem>

        <GridItem xs={12} md={4} pl={0} order={dynamicTextFields?.length > 0 ? 18 + dynamicTextFields?.length : 18}>
          <TextField
            label="Card Cost"
            value={formatCurrency(leaseData.cardCost)}
            disabled
            InputProps={{
              startAdornment: <InputAdornment position="start">{getCurrency(CURRENCY_CONSTANT.USD)}</InputAdornment>,
            }}
          />
        </GridItem>
      </GridContainer>

      {/* Tier Summary Section */}
      {showTierSummary && leaseData.tierSummary?.length > 0 && (
        <Box>
          <GridContainer my={2} className={classes.accountSummaryTitleContainer} gap={3}>
            <Typography variant="subtitle9">Tier Summary</Typography>
          </GridContainer>
          <GridContainer mt={3} className={classes.accountSummaryTitleFields} gap={3}>
            <GridItem xs={4} lg={3.5}>
              <Typography variant="subtitle4">Start Threshold</Typography>
            </GridItem>
            <GridItem xs={4} lg={3.5}>
              <Typography variant="subtitle4">Tier Threshold</Typography>
            </GridItem>
            <GridItem xs={4} lg={3.5}>
              <Typography variant="subtitle4">Tier Rate %</Typography>
            </GridItem>
            <GridItem xs={2} lg={2} />
          </GridContainer>

          {leaseData.tierSummary.map((tier, index) => {
            const startThreshold =
              index === 0 ? '0.01' : (Number(leaseData.tierSummary[index - 1]?.tierThreshold) + 0.01).toFixed(2);

            return (
              <GridContainer p={0} mb={3} className={classes.accountSummaryFields} key={index} gap={3}>
                <GridItem xs={4} lg={3.5} p={0}>
                  <TextField
                    value={startThreshold}
                    disabled
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">{getCurrency(CURRENCY_CONSTANT.USD)}</InputAdornment>
                      ),
                    }}
                  />
                </GridItem>

                <GridItem xs={4} lg={3.5}>
                  <TextField
                    value={formatCurrency(tier.tierThreshold)}
                    disabled
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">{getCurrency(CURRENCY_CONSTANT.USD)}</InputAdornment>
                      ),
                    }}
                  />
                </GridItem>

                <GridItem xs={2} lg={2} className={classes.debitCreditField}>
                  <TextField
                    value={formatPercentage(tier.tierRate)}
                    disabled
                    InputProps={{
                      endAdornment: <InputAdornment position="end">%</InputAdornment>,
                    }}
                  />
                </GridItem>
              </GridContainer>
            );
          })}
        </Box>
      )}
    </Box>
  );
};
